import type { SiteEventDetail, SiteEventItem, SiteEventName, SiteEventParams } from './site-events';

/**
 * Colonnes par régie de la table `SITE_EVENTS` : ce que reçoit chaque pixel NATIF quand le pro a
 * collé un ID dans ses réglages (sans GTM). Les noms publics du dataLayer ne sont pas concernés.
 *
 * Ajouter une régie = une colonne ici + le chargement de son SDK (`ConditionalTracking`) + sa
 * branche d'envoi (`SiteEventRouter`). Jamais de nom de régie dans un composant.
 */

export interface SiteEventDestination<T extends SiteEventName> {
  /** Nom de l'événement chez la régie. */
  name: string;
  params: (detail: SiteEventDetail<T>) => Record<string, unknown>;
  /** Condition d'envoi (défaut : toujours). */
  when?: (detail: SiteEventDetail<T>) => boolean;
  /** Identifiant de dédup navigateur / serveur (Meta : `eventID`, 4e argument de `fbq`). */
  event_id?: (detail: SiteEventDetail<T>) => string | undefined;
}

export interface SiteEventPlausibleDestination<T extends SiteEventName> {
  name: string;
  /** Liste blanche des paramètres transmis : jamais `user_data`, jamais d'identifiant. */
  props: readonly (keyof SiteEventParams[T] & string)[];
}

export interface SiteEventDestinationRow<T extends SiteEventName> {
  /** GA4 natif : les noms publics, sans traduction. */
  ga4: SiteEventDestination<T> | null;
  /** Google Ads natif : `conversion` + `send_to: 'AW-…/libellé'` saisi par le pro. */
  google_ads: SiteEventDestination<T> | null;
  meta: readonly SiteEventDestination<T>[] | null;
  tiktok: readonly SiteEventDestination<T>[] | null;
  plausible: SiteEventPlausibleDestination<T> | null;
}

export const SITE_EVENT_DESTINATION_COLUMNS = ['ga4', 'google_ads', 'meta', 'tiktok', 'plausible'] as const;
export type SiteEventDestinationColumn = (typeof SITE_EVENT_DESTINATION_COLUMNS)[number];

const GOOGLE_ADS_CONVERSION = 'conversion';

const NON_PARAM_KEYS: ReadonlySet<string> = new Set(['event', 'ecommerce', 'user_data']);

/** Paramètres à plat du contrat, sans `event`, `ecommerce` ni `user_data`. */
function flatParams<T extends SiteEventName>(detail: SiteEventDetail<T>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(detail).filter(([key]): boolean => !NON_PARAM_KEYS.has(key)));
}

/** GA4 natif = identité : même nom, mêmes paramètres que le contrat public. */
function ga4<T extends SiteEventName>(name: T): SiteEventDestination<T> {
  return { name, params: flatParams };
}

const noParams = (): Record<string, unknown> => ({});

const itemIds = (items: SiteEventItem[]): string[] => items.map((item): string => item.item_id);
const itemNames = (items: SiteEventItem[]): string => items.map((item): string => item.item_name).join(', ');

export const SITE_EVENT_DESTINATIONS: { [K in SiteEventName]: SiteEventDestinationRow<K> } = {
  generate_lead: {
    ga4: ga4('generate_lead'),
    google_ads: {
      name: GOOGLE_ADS_CONVERSION,
      params: (d): Record<string, unknown> => (d.lead_id ? { transaction_id: d.lead_id } : {}),
    },
    meta: [
      {
        name: 'Lead',
        params: (d): Record<string, unknown> => ({ form_type: d.form_type }),
        event_id: (d): string | undefined => d.lead_id,
      },
    ],
    tiktok: [{ name: 'SubmitForm', params: (d): Record<string, unknown> => ({ form_type: d.form_type }) }],
    plausible: { name: 'generate_lead', props: ['form_type', 'form_location'] },
  },
  booking_confirmed: {
    ga4: ga4('booking_confirmed'),
    google_ads: {
      name: GOOGLE_ADS_CONVERSION,
      params: (d): Record<string, unknown> => ({
        value: d.value,
        currency: d.currency,
        transaction_id: d.transaction_id,
      }),
    },
    meta: [
      // `Schedule` porte le total TTC ; `Purchase` ne part que si un montant a été débité en ligne.
      {
        name: 'Schedule',
        params: (d): Record<string, unknown> => ({ value: d.value, currency: d.currency }),
        event_id: (d): string => d.transaction_id,
      },
      {
        name: 'Purchase',
        when: (d): boolean => d.amount_paid > 0,
        params: (d): Record<string, unknown> => ({ value: d.amount_paid, currency: d.currency }),
        event_id: (d): string => d.transaction_id,
      },
    ],
    // TikTok : `Schedule` (prise de rendez-vous) et `Purchase` (ex-`CompletePayment`), même
    // découpage que Meta. Vérifié dans la doc TikTok « Supported standard events » le 01/10/2026.
    tiktok: [
      { name: 'Schedule', params: (d): Record<string, unknown> => ({ value: d.value, currency: d.currency }) },
      {
        name: 'Purchase',
        when: (d): boolean => d.amount_paid > 0,
        params: (d): Record<string, unknown> => ({ value: d.amount_paid, currency: d.currency }),
      },
    ],
    plausible: {
      name: 'booking_confirmed',
      props: ['value', 'currency', 'amount_paid', 'booking_type', 'promotion', 'discount'],
    },
  },
  purchase: {
    ga4: ga4('purchase'),
    google_ads: {
      name: GOOGLE_ADS_CONVERSION,
      params: (d): Record<string, unknown> => ({
        value: d.value,
        currency: d.currency,
        transaction_id: d.transaction_id,
      }),
    },
    meta: [
      {
        name: 'Purchase',
        params: (d): Record<string, unknown> => ({
          value: d.value,
          currency: d.currency,
          content_ids: itemIds(d.items),
          content_type: 'product',
        }),
        event_id: (d): string => d.transaction_id,
      },
    ],
    tiktok: [
      {
        name: 'Purchase',
        params: (d): Record<string, unknown> => ({
          value: d.value,
          currency: d.currency,
          content_id: itemIds(d.items)[0],
          content_type: 'product',
        }),
      },
    ],
    plausible: { name: 'purchase', props: ['value', 'currency'] },
  },
  phone_click: {
    ga4: ga4('phone_click'),
    google_ads: { name: GOOGLE_ADS_CONVERSION, params: noParams },
    meta: [{ name: 'Contact', params: (): Record<string, unknown> => ({ method: 'phone' }) }],
    tiktok: [{ name: 'Contact', params: (): Record<string, unknown> => ({ method: 'phone' }) }],
    plausible: { name: 'phone_click', props: ['click_location'] },
  },
  email_click: {
    ga4: ga4('email_click'),
    google_ads: { name: GOOGLE_ADS_CONVERSION, params: noParams },
    meta: [{ name: 'Contact', params: (): Record<string, unknown> => ({ method: 'email' }) }],
    tiktok: [{ name: 'Contact', params: (): Record<string, unknown> => ({ method: 'email' }) }],
    plausible: { name: 'email_click', props: ['click_location'] },
  },
  whatsapp_click: {
    ga4: ga4('whatsapp_click'),
    google_ads: { name: GOOGLE_ADS_CONVERSION, params: noParams },
    meta: [{ name: 'Contact', params: (): Record<string, unknown> => ({ method: 'whatsapp' }) }],
    tiktok: [{ name: 'Contact', params: (): Record<string, unknown> => ({ method: 'whatsapp' }) }],
    plausible: { name: 'whatsapp_click', props: ['click_location'] },
  },
  newsletter_subscribe: {
    ga4: ga4('newsletter_subscribe'),
    google_ads: { name: GOOGLE_ADS_CONVERSION, params: noParams },
    meta: [{ name: 'Subscribe', params: noParams }],
    tiktok: [{ name: 'Subscribe', params: noParams }],
    plausible: { name: 'newsletter_subscribe', props: [] },
  },
  add_to_cart: {
    ga4: ga4('add_to_cart'),
    // Pas une conversion : rien chez Google Ads.
    google_ads: null,
    meta: [
      {
        name: 'AddToCart',
        params: (d): Record<string, unknown> => ({
          content_ids: itemIds(d.items),
          content_name: itemNames(d.items),
          content_type: 'product',
          value: d.value,
          currency: d.currency,
        }),
      },
    ],
    tiktok: [
      {
        name: 'AddToCart',
        params: (d): Record<string, unknown> => ({
          content_id: itemIds(d.items)[0],
          content_name: itemNames(d.items),
          value: d.value,
          currency: d.currency,
        }),
      },
    ],
    plausible: { name: 'add_to_cart', props: ['value', 'currency'] },
  },
  /* Parcours du tunnel : GA4 natif (le pro qui y a branché son site voit l'entonnoir) et Plausible
     (entonnoir interne) ; aucune régie publicitaire, ce ne sont pas des conversions. */
  booking_started: {
    ga4: ga4('booking_started'),
    google_ads: null,
    meta: null,
    tiktok: null,
    plausible: { name: 'booking_started', props: ['prestation_count'] },
  },
  booking_step: {
    ga4: ga4('booking_step'),
    google_ads: null,
    meta: null,
    tiktok: null,
    plausible: { name: 'booking_step', props: ['step', 'step_index', 'step_total'] },
  },
  booking_slot_selected: {
    ga4: ga4('booking_slot_selected'),
    google_ads: null,
    meta: null,
    tiktok: null,
    plausible: { name: 'booking_slot_selected', props: ['lead_time_hours', 'promotion'] },
  },
  booking_prereserved: {
    ga4: ga4('booking_prereserved'),
    google_ads: null,
    meta: null,
    tiktok: null,
    plausible: { name: 'booking_prereserved', props: ['promotion'] },
  },
  booking_abandoned: {
    ga4: ga4('booking_abandoned'),
    google_ads: null,
    meta: null,
    tiktok: null,
    plausible: { name: 'booking_abandoned', props: ['last_step', 'reason'] },
  },
  /* Entrée dans le tunnel avec une sélection : signal d'intention reconnu par les régies (InitiateCheckout),
     jamais une conversion. */
  begin_checkout: {
    ga4: ga4('begin_checkout'),
    google_ads: null,
    meta: [
      {
        name: 'InitiateCheckout',
        params: (d): Record<string, unknown> => ({
          content_ids: itemIds(d.items),
          content_type: 'product',
          num_items: d.items.reduce((sum, item): number => sum + item.quantity, 0),
          value: d.value,
          currency: d.currency,
        }),
      },
    ],
    tiktok: [
      {
        name: 'InitiateCheckout',
        params: (d): Record<string, unknown> => ({
          content_id: itemIds(d.items)[0],
          content_type: 'product',
          value: d.value,
          currency: d.currency,
        }),
      },
    ],
    plausible: { name: 'begin_checkout', props: ['value', 'currency', 'promotion'] },
  },
};

export interface ResolvedSiteEventDestination {
  name: string;
  params: Record<string, unknown>;
  event_id?: string;
}

/** Applique une colonne à un événement émis : la liste (éventuellement vide) des envois à faire. */
export function resolveSiteEventDestinations<T extends SiteEventName>(
  column: SiteEventDestination<T> | readonly SiteEventDestination<T>[] | null,
  detail: SiteEventDetail<T>,
): ResolvedSiteEventDestination[] {
  if (!column) return [];
  const destinations: readonly SiteEventDestination<T>[] = Array.isArray(column)
    ? column
    : [column as SiteEventDestination<T>];
  return destinations
    .filter((destination): boolean => destination.when?.(detail) ?? true)
    .map((destination): ResolvedSiteEventDestination => {
      const eventId = destination.event_id?.(detail);
      return {
        name: destination.name,
        params: destination.params(detail),
        ...(eventId ? { event_id: eventId } : {}),
      };
    });
}

/** Props Plausible d'un événement : liste blanche de la colonne, valeurs scalaires seulement. */
export function resolvePlausibleProps<T extends SiteEventName>(
  destination: SiteEventPlausibleDestination<T>,
  detail: SiteEventDetail<T>,
): Record<string, string | number | boolean> {
  const source = detail as Record<string, unknown>;
  const props: Record<string, string | number | boolean> = {};
  for (const key of destination.props) {
    const value = source[key];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') props[key] = value;
  }
  return props;
}
